import { range } from 'lodash';
import type { RiskInsightsSiteRow } from 'src/app/portfolio/state/api/riskInsightsSitesQuery';

export type FetchQuadrantPage = (page: number) => Promise<{ items: RiskInsightsSiteRow[]; totalPages: number }>;

export type FetchPortfolioSitesResult = {
    items: RiskInsightsSiteRow[];
    truncated: boolean;
};

export async function fetchPortfolioSitesForQuadrant(
    fetchPage: FetchQuadrantPage,
    maxPagesInput: number | Promise<number> = 10,
    onProgress?: (pageItems: RiskInsightsSiteRow[]) => void // called with each page's own items as it resolves, not the cumulative list
): Promise<FetchPortfolioSitesResult> {
    const first = await fetchPage(1);
    const maxPages = await maxPagesInput;
    const truncated = first.totalPages > maxPages;
    const effectiveTotalPages = Math.min(first.totalPages, maxPages);

    if (truncated) {
        console.warn(
            `fetchPortfolioSitesForQuadrant: portfolio has ${first.totalPages} pages but only ${maxPages} were fetched (${
                maxPages * 100
            } sites shown). Increase maxPages to show more.`
        );
    }

    onProgress?.(first.items);

    if (effectiveTotalPages <= 1) return { items: first.items, truncated };

    const remainingPages = range(2, effectiveTotalPages + 1);
    const rest = await Promise.all(
        remainingPages.map(page =>
            fetchPage(page).then(result => {
                onProgress?.(result.items);
                return result;
            })
        )
    );

    return { items: [...first.items, ...rest.flatMap(r => r.items)], truncated };
}
